import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import {
  Code,
  FormatIndentIncrease,
  FormatIndentDecrease,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';

// Função para sanitizar código (remover elementos perigosos)
const sanitizeCode = (code, language = 'text') => {
  if (!code) return '';
  
  // Para JSON, validar se é JSON válido
  if (language === 'json') {
    try {
      JSON.parse(code);
    } catch (e) {
      // Se não for JSON válido, escapar caracteres perigosos
      return code
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }
  
  // Escapar caracteres HTML perigosos
  return code
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/&/g, '&amp;');
};

// Função para formatação básica de código
const formatCode = (code, language) => {
  if (!code) return '';
  
  switch (language) {
    case 'json':
      try {
        return JSON.stringify(JSON.parse(code), null, 2);
      } catch (e) {
        return code;
      }
    case 'javascript':
    case 'js':
      // Formatação básica para JavaScript
      return code
        .replace(/;/g, ';\n')
        .replace(/{/g, '{\n  ')
        .replace(/}/g, '\n}')
        .replace(/,/g, ',\n  ');
    default:
      return code;
  }
};

// Função para syntax highlighting básico
const applySyntaxHighlighting = (code, language) => {
  if (!code) return '';
  
  switch (language) {
    case 'json':
      return code
        .replace(/(".*?")\s*:/g, '<span style="color: #0451a5; font-weight: bold;">$1</span>:')
        .replace(/:\s*(".*?")/g, ': <span style="color: #008000;">$1</span>')
        .replace(/:\s*(\d+)/g, ': <span style="color: #0451a5;">$1</span>')
        .replace(/:\s*(true|false|null)/g, ': <span style="color: #0000ff;">$1</span>');
    
    case 'javascript':
    case 'js':
      return code
        .replace(/\b(function|const|let|var|if|else|for|while|return|true|false|null|undefined)\b/g, 
          '<span style="color: #af00db; font-weight: bold;">$1</span>')
        .replace(/(".*?"|'.*?')/g, '<span style="color: #008000;">$1</span>')
        .replace(/\/\/.*$/gm, '<span style="color: #6a9955; font-style: italic;">$&</span>')
        .replace(/\/\*[\s\S]*?\*\//g, '<span style="color: #6a9955; font-style: italic;">$&</span>');
    
    case 'css':
      return code
        .replace(/([a-zA-Z-]+)\s*:/g, '<span style="color: #0451a5;">$1</span>:')
        .replace(/:\s*([^;]+);/g, ': <span style="color: #008000;">$1</span>;')
        .replace(/\/\*[\s\S]*?\*\//g, '<span style="color: #6a9955; font-style: italic;">$&</span>');
    
    default:
      return code;
  }
};

const SecureCodeEditor = ({
  value = '',
  onChange,
  language = 'text',
  height = 400,
  readOnly = false,
  showLineNumbers = true,
  theme = 'light',
  placeholder = 'Digite seu código aqui...',
  sx = {}
}) => {
  const [code, setCode] = useState(value);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCode(value);
  }, [value]);

  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    const sanitizedCode = sanitizeCode(newCode, language);
    setCode(sanitizedCode);
    if (onChange) {
      onChange(sanitizedCode);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Falha ao copiar:', err);
    }
  };

  const handleFormat = () => {
    const formattedCode = formatCode(code, language);
    setCode(formattedCode);
    if (onChange) {
      onChange(formattedCode);
    }
  };

  const handleIndent = () => {
    const textarea = document.getElementById('code-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = code.substring(start, end);
    const indentedText = selectedText.split('\n').map(line => '  ' + line).join('\n');
    
    const newCode = code.substring(0, start) + indentedText + code.substring(end);
    setCode(newCode);
    if (onChange) {
      onChange(newCode);
    }
  };

  const handleUnindent = () => {
    const textarea = document.getElementById('code-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = code.substring(start, end);
    const unindentedText = selectedText.split('\n').map(line => 
      line.startsWith('  ') ? line.substring(2) : line
    ).join('\n');
    
    const newCode = code.substring(0, start) + unindentedText + code.substring(end);
    setCode(newCode);
    if (onChange) {
      onChange(newCode);
    }
  };

  const handleKeyDown = (e) => {
    // Tab para indentação
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      
      if (e.shiftKey) {
        // Shift+Tab para desindentar
        const beforeCursor = code.substring(0, start);
        const afterCursor = code.substring(end);
        const currentLine = beforeCursor.split('\n').pop();
        
        if (currentLine.startsWith('  ')) {
          const newBeforeCursor = beforeCursor.substring(0, beforeCursor.lastIndexOf(currentLine)) + currentLine.substring(2);
          const newCode = newBeforeCursor + afterCursor;
          setCode(newCode);
          if (onChange) onChange(newCode);
          
          // Reposicionar cursor
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = start - 2;
          }, 0);
        }
      } else {
        // Tab normal
        const newCode = code.substring(0, start) + '  ' + code.substring(end);
        setCode(newCode);
        if (onChange) onChange(newCode);
        
        // Reposicionar cursor
        setTimeout(() => {
          e.target.selectionStart = e.target.selectionEnd = start + 2;
        }, 0);
      }
    }
  };

  // Calcular número de linhas para mostrar números
  const lineCount = code ? code.split('\n').length : 1;
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1);

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {/* Toolbar */}
      <Paper elevation={1} sx={{ p: 1, mb: 1, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Linguagem</InputLabel>
            <Select
              value={language}
              label="Linguagem"
              disabled={readOnly}
            >
              <MenuItem value="text">Texto</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="javascript">JavaScript</MenuItem>
              <MenuItem value="css">CSS</MenuItem>
              <MenuItem value="html">HTML</MenuItem>
            </Select>
          </FormControl>

          <Button
            size="small"
            onClick={handleFormat}
            disabled={readOnly}
            startIcon={<Code />}
          >
            Formatar
          </Button>

          <Tooltip title="Indentar">
            <IconButton size="small" onClick={handleIndent} disabled={readOnly}>
              <FormatIndentIncrease />
            </IconButton>
          </Tooltip>

          <Tooltip title="Desindentar">
            <IconButton size="small" onClick={handleUnindent} disabled={readOnly}>
              <FormatIndentDecrease />
            </IconButton>
          </Tooltip>

          <Tooltip title={copied ? "Copiado!" : "Copiar"}>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? <CheckCircle color="success" /> : <ContentCopy />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Editor */}
      <Paper 
        variant="outlined" 
        sx={{ 
          display: 'flex',
          height,
          overflow: 'hidden'
        }}
      >
        {/* Line numbers */}
        {showLineNumbers && (
          <Box
            sx={{
              width: 50,
              backgroundColor: '#f5f5f5',
              borderRight: '1px solid #e0e0e0',
              padding: '8px 4px',
              fontSize: '12px',
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              color: '#666',
              textAlign: 'right',
              userSelect: 'none',
              overflow: 'hidden'
            }}
          >
            {lineNumbers.map(num => (
              <div key={num} style={{ height: '18px', lineHeight: '18px' }}>
                {num}
              </div>
            ))}
          </Box>
        )}

        {/* Code area */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <textarea
            id="code-editor"
            value={code}
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            readOnly={readOnly}
            placeholder={placeholder}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              padding: '8px',
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: '14px',
              lineHeight: '18px',
              backgroundColor: 'transparent',
              color: theme === 'dark' ? '#ffffff' : '#000000'
            }}
          />
          
          {!code && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                pointerEvents: 'none',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '14px'
              }}
            >
              {placeholder}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SecureCodeEditor;